import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import useAppState, { useNetworkState } from "../hooks/appStateHook";
import { runNetworkRequest } from "../hooks/networkHook";
import { removeEntityDelta, usePendingUpdates } from "../reducers/pendingUpdateSlice";
import useClientConfig from "../hooks/clientConfigHook";
import { useApp } from "./AppProvider";

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const OfflineProcessingContext = createContext({ pendingCount: 0 });

export const useOfflineProcessing = () => useContext(OfflineProcessingContext);

const OfflineProcessor = ({ children }) => {
    const clientConfig = useClientConfig();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pending = usePendingUpdates();
    const App = useApp();
    const { isOnline, wsStatus } = useNetworkState();
    const { token, refreshToken } = useAppState();
    
    const processing = useRef(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const debounce = useRef();
    const [autoprocess, setAutoprocess] = useState(true);
    const itemsToProcess = useRef();
    const wasOnline = useRef(isOnline && wsStatus === 'authenticated');
    const [status, setStatus] = useState({ online: wasOnline.current, count: 0 });
    
    const processPending = useCallback(async () => {
        processing.current = true;
        setIsProcessing(true);
        for (const op of itemsToProcess.current) {
            const result = await runNetworkRequest(App, dispatch, navigate, null, token, refreshToken, { url: op.url, method: op.method, body: op.body ?? op.delta });

            if (result?.success) {
                if (op.successAction) {
                    dispatch({ type: op.successAction, payload: result.data });
                }
            } else if (result?.success === false) {
                if (op.failureAction) {
                    dispatch({ type: op.failureAction, payload: result.data });
                }
            } else { // network failure - go offline
                setStatus(status => ({ ...status, online: false }))
                break;                    
            }
            setStatus(status => ({ ...status, count: status.count - 1 }));
            dispatch(removeEntityDelta(op.requestId, op.entity, op.pk, op.fk));
            sleep(50);
            if (!processing.current) {
                break;
            }
        }

        setStatus(status => ({ ...status, count: 0 }));
        processing.current = false;
        setIsProcessing(false);
    }, [dispatch, navigate, token, refreshToken, App]);

    useEffect(() => {
        if (!clientConfig.enableOfflineMode) {
            return;
        }
        const cameOnline = !wasOnline.current && isOnline && wsStatus === 'authenticated';
        const fellOffline = wasOnline.current && (!isOnline || wsStatus !== 'authenticated');
        wasOnline.current = isOnline && wsStatus === 'authenticated';

        if (cameOnline && !processing.current && pending.length > 0) {
            itemsToProcess.current = [...pending]; // snapshot
            setStatus({ online: true, count: itemsToProcess.current.length });
            if (autoprocess) {
                processing.current = true;
                setIsProcessing(true);
                debounce.current = setTimeout(async () => {
                    debounce.current = null;
                    processPending();
                }, 5000);
            }
        } else if (fellOffline && debounce.current) {
            clearTimeout(debounce.current);
            debounce.current = null;
            processing.current = false;
            setIsProcessing(false);
            setStatus({ online: false, count: 0 });
        }
    }, [isOnline, wsStatus, pending, autoprocess, clientConfig.enableOfflineMode, processPending]);

    const cancelProcessing = useCallback(() => {
        processing.current = false;
        setIsProcessing(false);
    }, []);

    const startProcessing = useCallback(() => {
        if (itemsToProcess.current.length > 0) {
            processPending()
        }
    }, [processPending]);

    const toggleAutoprocess = useCallback(() => setAutoprocess(autoprocess => !autoprocess), []);

    const value = useMemo(() => ({ ...status, isProcessing, autoprocess, startProcessing, cancelProcessing, toggleAutoprocess }), [status, isProcessing, autoprocess, startProcessing, cancelProcessing, toggleAutoprocess]);

    return <OfflineProcessingContext.Provider value={value}>
        {children}
    </OfflineProcessingContext.Provider>
}

export default OfflineProcessor;